import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useCallStore } from '@/stores/call';
import { useChatPreferenceStore } from '@/stores/chatPreference';
import { useToastStore } from '@/stores/toast.js';
import { PreferenceKey, ScreenPreference, SubtitlePreference, ValidationRule } from '@/types/components';

export const useChatPreferences = () => {
  const { t } = useI18n();

  const toastStore = useToastStore();
  const callStore = useCallStore();
  const chatPreferences = useChatPreferenceStore();

  const isGsc = computed<boolean>(() => {
    return !!callStore.qrCodeInfo.options.screenHeight;
  });

  const validationRules = computed<Record<PreferenceKey, ValidationRule>>(() => {
    return {
      screenMessageWidth: {
        validate: (v) => {
          if (v < 20) {
            return t('error_input_too_small', { errorTarget: t('screen_mode_message_width'), minimum: 20 });
          }
          if (v > 100) {
            return t('error_input_too_big', { errorTarget: t('screen_mode_message_width'), maximum: 100 });
          }
          return '';
        },
        minimum: 20,
        maximum: 100,
      },
      screenContainerHeight: {
        validate: (v) => {
          if (isGsc.value && v < 500) {
            return t('error_input_too_small', { errorTarget: t('screen_mode_container_height'), minimum: 500 });
          }
          if (isGsc.value && v > 2000) {
            return t('error_input_too_big', { errorTarget: t('screen_mode_container_height'), maximum: 2000 });
          }
          return '';
        },
        minimum: 500,
        maximum: 2000,
      },
      subtitleWidth: {
        validate: (v) => {
          if (v < 20) {
            return t('error_input_too_small', { errorTarget: t('subtitle_mode_message_width'), minimum: 500 });
          }
          if (v > 100) {
            return t('error_input_too_big', { errorTarget: t('subtitle_mode_message_width'), maximum: 100 });
          }
          return '';
        },
        minimum: 20,
        maximum: 100,
      },
      subtitleBackgroundColor: {
        validate: (v) => {
          if (!['GREEN', 'BLACK'].includes(v.toUpperCase())) {
            return t('green_or_black', { errorTarget: t('screen_color') });
          }
          return '';
        },
      },
    };
  });

  const getValidationError = (newVal: Record<string, any>) => {
    for (const [key, value] of Object.entries(newVal)) {
      const rule = validationRules.value[key as PreferenceKey];
      if (rule && rule.validate(value)) {
        return rule.validate(value);
      }
    }
    return null;
  };

  const isValidPreference = (newVal: Record<string, any>) => {
    const errorMessage = getValidationError(newVal);
    if (errorMessage) {
      toastStore.error(errorMessage);
      return false;
    }
    return true;
  };

  const updateScreenPreferences = (preferences: ScreenPreference) => {
    if (!isValidPreference(preferences)) {
      return;
    }
    chatPreferences.updateScreenPreference(preferences);
  };

  const updateSubtitlePreferences = (preferences: SubtitlePreference) => {
    if (!isValidPreference(preferences)) {
      return;
    }
    chatPreferences.updateSubtitlePreference(preferences);
  };

  return {
    validationRules,
    isValidPreference,
    getValidationError,
    updateScreenPreferences,
    updateSubtitlePreferences,
  };
};
